import { Button, Icon } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { useApplicationTableState } from 'contexts/ApplicationTableStateContext/ApplicationTableStateContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { Tooltip } from 'react-tooltip';
import {
  APPLICATION_STATUS_LABEL,
  APPLICATION_TABLE_KEYS,
} from 'utils/applications/applications.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './ExportCSVButton.module.css';

export const ExportCSVButton = () => {
  const { gridApi, status } = useApplicationTableState();
  const { i18n } = useLocale();
  const numberFormat = useNumberFormat();
  const { trackEvent } = useAmplitude();

  const formatDate = (value?: string) =>
    value
      ? i18n.date(value, { day: '2-digit', month: 'short', year: '2-digit' })
      : '-';

  const formatCurrency = (value?: string) =>
    value ? numberFormat.formatCurrency(value) : '-';

  const exportToCsv = () => {
    trackEvent(AMPLITUDE_EVENTS.exportCsvButtonClicked);

    gridApi?.exportDataAsCsv({
      fileName: `${t`export`}_${new Date()
        .toLocaleDateString()
        .replaceAll('/', '-')}_${new Date()
        .toLocaleTimeString()
        .replaceAll(':', '-')}`,
      processCellCallback: ({ column, value }) => {
        switch (column.getColId()) {
          case APPLICATION_TABLE_KEYS.created:
            return formatDate(value);
          case APPLICATION_TABLE_KEYS.company_name:
            return value.registered_company_name;
          case APPLICATION_TABLE_KEYS.customer_name:
            return t`[Anonymised]`;
          case APPLICATION_TABLE_KEYS.expiry:
            return formatDate(value);
          case APPLICATION_TABLE_KEYS.approval_amount:
            return formatCurrency(value);
          case APPLICATION_TABLE_KEYS.requested_amount:
            return formatCurrency(value);
          case APPLICATION_TABLE_KEYS.status:
            return APPLICATION_STATUS_LABEL(value);
          default:
            return value;
        }
      },
    });
  };

  return (
    <Button
      disabled={!gridApi || status !== 'success'}
      onClick={exportToCsv}
      variant="secondary-alt"
      data-tooltip-id="export-csv"
      size="small"
    >
      <Icon icon="download" />
      <Trans>Export as CSV</Trans>

      {/* TODO: Replace with Orion's upcoming dynamic tooltip when its built and uninstall react-tooltip  */}
      <Tooltip
        className={styles.tooltip}
        id="export-csv"
        opacity={1}
        content={t`Export the current list of applications.`}
        openEvents={{ focus: false, mouseenter: true }}
        place="bottom"
      />
    </Button>
  );
};
